import { createClient } from '@supabase/supabase-js';
import { config } from '@/lib/config';
import type { Database } from '@/types/database';

export const supabase = createClient<Database>(
  config.supabase.url,
  config.supabase.anonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  }
);

// Direct fetch helper to bypass blocking Supabase client
export const fetchSupabase = async (
  table: string, 
  options: {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    filters?: string;
    body?: unknown;
    select?: string;
    single?: boolean;
    accessToken?: string;
  } = {}
) => {
  const { method = 'GET', filters = '', body, select = '*', single = false } = options;
  const url = `${config.supabase.url}/rest/v1/${table}?select=${select}${filters ? `&${filters}` : ''}`;
  
  // The SDK refreshes expired tokens; callers inside an auth event pass its token.
  const accessToken = options.accessToken ??
    (await supabase.auth.getSession()).data.session?.access_token ?? config.supabase.anonKey;

  try {
    const response = await fetch(url, {
      method,
      headers: {
        'apikey': config.supabase.anonKey,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    
    if (!response.ok) {
      const error = await response.json();
      console.error('[SUPABASE] Request failed:', { method, table, status: response.status, error });
      return { data: null, error };
    }
    
    const data = response.status === 204 ? null : await response.json();
    return { data: single ? data?.[0] ?? null : data, error: null };
  } catch (error) {
    console.error('[SUPABASE] Fetch error:', error);
    return { data: null, error };
  }
};

// Auth helpers
export const auth = {
  signUp: async (email: string, password: string, metadata?: Record<string, unknown>) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: `${window.location.origin}/login`,
      },
    });
    return { data, error };
  },

  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  getSession: async () => {
    const { data, error } = await supabase.auth.getSession();
    return { data: data.session, error };
  },

  getUser: async () => {
    const { data, error } = await supabase.auth.getUser();
    return { data: data?.user ?? null, error };
  },

  resetPassword: async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  },

  updatePassword: async (newPassword: string) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { data, error };
  },
};

// Database helpers (using direct fetch to avoid blocking)
export const db = {
  // Profiles
  profiles: {
    get: async (id: string) => {
      return fetchSupabase('nutry_profiles', {
        filters: `id=eq.${id}`,
        single: true,
      });
    },
    update: async (id: string, updates: Partial<Database['public']['Tables']['nutry_profiles']['Update']>) => {
      return fetchSupabase('nutry_profiles', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: updates,
        single: true,
      });
    },
  },

  // Patients
  patients: {
    list: async (nutritionistId: string) => {
      return fetchSupabase('nutry_patients', {
        filters: `nutritionist_id=eq.${nutritionistId}&deleted_at=is.null&order=last_name`,
      });
    },
    get: async (id: string) => {
      return fetchSupabase('nutry_patients', {
        filters: `id=eq.${id}`,
        single: true,
      });
    },
    create: async (patient: Database['public']['Tables']['nutry_patients']['Insert']) => {
      return fetchSupabase('nutry_patients', {
        method: 'POST',
        body: patient,
        single: true,
      });
    },
    update: async (id: string, updates: Database['public']['Tables']['nutry_patients']['Update']) => {
      return fetchSupabase('nutry_patients', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: updates,
        single: true,
      });
    },
    delete: async (id: string) => {
      return fetchSupabase('nutry_patients', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: { deleted_at: new Date().toISOString() },
      });
    },
  },

  // Measurements
  measurements: {
    list: async (patientId: string) => {
      return fetchSupabase('nutry_measurements', {
        filters: `patient_id=eq.${patientId}&order=measured_at.desc`,
      });
    },
    create: async (measurement: Database['public']['Tables']['nutry_measurements']['Insert']) => {
      return fetchSupabase('nutry_measurements', {
        method: 'POST',
        body: measurement,
        single: true,
      });
    },
    getLatest: async (patientId: string) => {
      return fetchSupabase('nutry_measurements', {
        filters: `patient_id=eq.${patientId}&order=measured_at.desc&limit=1`,
        single: true,
      });
    },
  },

  // Foods
  foods: {
    search: async (query: string, limit = 50) => {
      return fetchSupabase('nutry_foods', {
        filters: `name=ilike.*${query}*&is_active=eq.true&limit=${limit}`,
      });
    },
    getByCategory: async (categoryId: string) => {
      return fetchSupabase('nutry_foods', {
        filters: `category_id=eq.${categoryId}&is_active=eq.true&order=name`,
      });
    },
    get: async (id: string) => {
      return fetchSupabase('nutry_foods', {
        filters: `id=eq.${id}`,
        single: true,
      });
    },
  },

  // Food Categories
  foodCategories: {
    list: async () => {
      return fetchSupabase('nutry_food_categories', {
        filters: `order=sort_order`,
      });
    },
  },

  // Diets
  diets: {
    list: async (nutritionistId: string) => {
      return fetchSupabase('nutry_diets', {
        select: '*,patient:nutry_patients(id,first_name,last_name)',
        filters: `nutritionist_id=eq.${nutritionistId}&order=created_at.desc`,
      });
    },
    listByPatient: async (patientId: string) => {
      return fetchSupabase('nutry_diets', {
        filters: `patient_id=eq.${patientId}&order=created_at.desc`,
      });
    },
    get: async (id: string) => {
      return fetchSupabase('nutry_diets', {
        select: '*,patient:nutry_patients(*),nutry_diet_days(*,nutry_diet_meals(*,nutry_diet_meal_items(*)))',
        filters: `id=eq.${id}`,
        single: true,
      });
    },
    create: async (diet: Database['public']['Tables']['nutry_diets']['Insert']) => {
      return fetchSupabase('nutry_diets', {
        method: 'POST',
        body: diet,
        single: true,
      });
    },
    update: async (id: string, updates: Database['public']['Tables']['nutry_diets']['Update']) => {
      return fetchSupabase('nutry_diets', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: updates,
        single: true,
      });
    },
    delete: async (id: string) => {
      return fetchSupabase('nutry_diets', {
        method: 'DELETE',
        filters: `id=eq.${id}`,
      });
    },
  },

  // Diet Days
  dietDays: {
    listByDiet: async (dietId: string) => {
      return fetchSupabase('nutry_diet_days', {
        filters: `diet_id=eq.${dietId}&order=day_number`,
      });
    },
    create: async (day: Database['public']['Tables']['nutry_diet_days']['Insert']) => {
      return fetchSupabase('nutry_diet_days', {
        method: 'POST',
        body: day,
        single: true,
      });
    },
    update: async (id: string, updates: Database['public']['Tables']['nutry_diet_days']['Update']) => {
      return fetchSupabase('nutry_diet_days', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: updates,
        single: true,
      });
    },
  },

  // Diet Meals
  dietMeals: {
    listByDay: async (dayId: string) => {
      return fetchSupabase('nutry_diet_meals', {
        filters: `diet_day_id=eq.${dayId}&order=sort_order`,
      });
    },
    create: async (meal: Database['public']['Tables']['nutry_diet_meals']['Insert']) => {
      return fetchSupabase('nutry_diet_meals', {
        method: 'POST',
        body: meal,
        single: true,
      });
    },
    update: async (id: string, updates: Database['public']['Tables']['nutry_diet_meals']['Update']) => {
      return fetchSupabase('nutry_diet_meals', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: updates,
        single: true,
      });
    },
  },

  // Diet Meal Items
  dietMealItems: {
    listByMeal: async (mealId: string) => {
      return fetchSupabase('nutry_diet_meal_items', {
        select: '*,food:nutry_foods(*)',
        filters: `diet_meal_id=eq.${mealId}&order=sort_order`,
      });
    },
    create: async (item: Database['public']['Tables']['nutry_diet_meal_items']['Insert']) => {
      return fetchSupabase('nutry_diet_meal_items', {
        method: 'POST',
        select: '*,food:nutry_foods(*)',
        body: item,
        single: true,
      });
    },
    update: async (id: string, updates: Database['public']['Tables']['nutry_diet_meal_items']['Update']) => {
      return fetchSupabase('nutry_diet_meal_items', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: updates,
        single: true,
      });
    },
    delete: async (id: string) => {
      return fetchSupabase('nutry_diet_meal_items', {
        method: 'DELETE',
        filters: `id=eq.${id}`,
      });
    },
    deleteByMeal: async (mealId: string) => {
      return fetchSupabase('nutry_diet_meal_items', {
        method: 'DELETE',
        filters: `diet_meal_id=eq.${mealId}`,
      });
    },
  },

  // Anamnesis
  anamnesis: {
    get: async (patientId: string) => {
      return fetchSupabase('nutry_anamnesis', {
        filters: `patient_id=eq.${patientId}&order=recorded_at.desc&limit=1`,
        single: true,
      });
    },
    create: async (anamnesis: Database['public']['Tables']['nutry_anamnesis']['Insert']) => {
      return fetchSupabase('nutry_anamnesis', {
        method: 'POST',
        body: anamnesis,
        single: true,
      });
    },
    update: async (id: string, updates: Database['public']['Tables']['nutry_anamnesis']['Update']) => {
      return fetchSupabase('nutry_anamnesis', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: updates,
        single: true,
      });
    },
  },

  // Food Anamnesis
  foodAnamnesis: {
    get: async (patientId: string) => {
      return fetchSupabase('nutry_food_anamnesis', {
        filters: `patient_id=eq.${patientId}&order=recorded_at.desc&limit=1`,
        single: true,
      });
    },
    create: async (foodAnamnesis: Database['public']['Tables']['nutry_food_anamnesis']['Insert']) => {
      return fetchSupabase('nutry_food_anamnesis', {
        method: 'POST',
        body: foodAnamnesis,
        single: true,
      });
    },
    update: async (id: string, updates: Database['public']['Tables']['nutry_food_anamnesis']['Update']) => {
      return fetchSupabase('nutry_food_anamnesis', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: updates,
        single: true,
      });
    },
  },

  // Appointments
  appointments: {
    list: async (nutritionistId: string) => {
      return fetchSupabase('nutry_appointments', {
        select: '*,patient:nutry_patients(id,first_name,last_name)',
        filters: `nutritionist_id=eq.${nutritionistId}&order=scheduled_at`,
      });
    },
    listByPatient: async (patientId: string) => {
      return fetchSupabase('nutry_appointments', {
        filters: `patient_id=eq.${patientId}&order=scheduled_at.desc`,
      });
    },
    create: async (appointment: Database['public']['Tables']['nutry_appointments']['Insert']) => {
      return fetchSupabase('nutry_appointments', {
        method: 'POST',
        body: appointment,
        single: true,
      });
    },
    update: async (id: string, updates: Database['public']['Tables']['nutry_appointments']['Update']) => {
      return fetchSupabase('nutry_appointments', {
        method: 'PATCH',
        filters: `id=eq.${id}`,
        body: updates,
        single: true,
      });
    },
    delete: async (id: string) => {
      return fetchSupabase('nutry_appointments', {
        method: 'DELETE',
        filters: `id=eq.${id}`,
      });
    },
  },
};
